import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, socket } from "../api";
import Navbar2 from "../components/navbar/Navbar2";
import AOS from "aos";
import "aos/dist/aos.css";
import LoadingPage from "./LoadingPage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { parseJWT } from "../utils/jwtUtils";
import Footer from "../components/navbar/Footer";

const SelectCardsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get("room");
  const stake = searchParams.get("stake");
  const winAmount = searchParams.get("win");
  const [roomInfo, setRoomInfo] = useState(null);
  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const userInfo = parseJWT(token?.token);

  const playerPhone = userInfo?.phone;

  const [cards, setCards] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [reserving, setReserving] = useState(false);

  const pendingCardRef = useRef(null);
  const reserveTimeoutRef = useRef(null);

  // Init AOS
  useEffect(() => {
    AOS.init({ duration: 500, easing: "ease-in-sine" });
  }, []);
  const roomInfoRef = useRef(null);

  useEffect(() => {
    roomInfoRef.current = roomInfo;
  }, [roomInfo]);
  // Load cards
  useEffect(() => {
    if (!roomId) return;

    const loadCards = async () => {
      try {
        const res = await api.get(`/api/cards?roomId=${roomId}`);
        setCards(res.data || []);
      } catch (err) {
        console.error("❌ Error fetching cards:", err);
      }
    };

    loadCards();
  }, [roomId]);

  // Join room
  useEffect(() => {
    if (playerPhone && roomId && stake) {
      socket.emit("join-room", { roomId, phone: playerPhone, amount: stake });
    }
  }, [playerPhone, roomId, stake]);

  // Auto release pending reservation
  const releasePendingCard = () => {
    if (pendingCardRef.current) {
      socket.emit("cancel-reservation", {
        number: pendingCardRef.current,
        roomId,
        phone: playerPhone,
      });
      pendingCardRef.current = null;
      setReserving(false);
    }
  };

  // Release on unmount / refresh
  useEffect(() => {
    const releaseOnUnload = () => releasePendingCard();

    window.addEventListener("beforeunload", releaseOnUnload);
    return () => {
      releaseOnUnload();
      window.removeEventListener("beforeunload", releaseOnUnload);
    };
  }, []);

  // Socket listeners
  useEffect(() => {
    const handleCardUpdated = (updatedCard) => {
      setCards((prev) =>
        prev.map((c) => (c.number === updatedCard.number ? updatedCard : c)),
      );

      if (
        updatedCard.status === "reserved" &&
        updatedCard.reservedBy === playerPhone
      ) {
        clearTimeout(reserveTimeoutRef.current);
        pendingCardRef.current = null;
        setReserving(false);

        navigate(
          `/confirm/${updatedCard.number}?room=${roomId}&stake=${stake}&win=${winAmount}`,
        );
      }
    };

    const handleReserveFailed = ({ number, reason }) => {
      clearTimeout(reserveTimeoutRef.current);
      pendingCardRef.current = null;
      setReserving(false);
    };

    const handleReserveSuccess = () => {};

    socket.on("card-updated", handleCardUpdated);
    socket.on("card-reserve-failed", handleReserveFailed);
    socket.on("card-reserved-success", handleReserveSuccess);

    socket.on("room-countdown", ({ roomId: id, countdown }) => {
      if (id === roomId) setTimeLeft(countdown);
    });

    socket.on("game-started", ({ roomId: id }) => {
      if (id === roomId) {
        // console.log("Before playing (from ref):", roomInfoRef.current);
        // Navigate to /play and pass latest roomInfo
        if (roomInfoRef.current) {
          navigate("/play", { state: { roomInfo: roomInfoRef.current } });
        }
      }
    });

    return () => {
      socket.off("card-updated", handleCardUpdated);
      socket.off("card-reserve-failed", handleReserveFailed);
      socket.off("card-reserved-success", handleReserveSuccess);
      socket.off("room-countdown");
      socket.off("game-started");
    };
  }, [playerPhone, roomId, stake, winAmount, navigate]);

  // Click handler
  const handleCardClick = (number) => {
    if (reserving) return;
    // ❌ Check if room is already playing
    if (roomInfo?.status === "playing") {
      toast.error("የዚህ ዙር ጨዋታ ተጀምሯል። እባክዎ ትንሽ ይጠብቁ!");
      return;
    }
    const selected = cards.find((c) => c.number === number);
    if (!selected || selected.status !== "available") {
      toast.error(" ይህ ካርቴላ ለጊዜው ተይዙዋል!");
      return;
    }

    setReserving(true);
    pendingCardRef.current = number;

    // Auto release after 3 seconds if no navigate
    reserveTimeoutRef.current = setTimeout(() => {
      releasePendingCard();
    }, 3000);

    socket.emit("reserve-card", { number, roomId, phone: playerPhone });
  };

  useEffect(() => {
    if (!roomId) return;

    // Subscribe to room
    socket.emit("subscribe-room", { roomId });

    // Listen for live room updates
    const handleRoomInfo = (roomData) => {
      setRoomInfo(roomData); // Update state
    };

    socket.on("room-info", handleRoomInfo);

    // Cleanup on unmount
    return () => {
      socket.off("room-info", handleRoomInfo);
    };
  }, [roomId]);

  useEffect(() => {
    socket.on("single-card-timeout", (data) => {
      toast.success(
        `Refund Successful! ${data.refund} ETB has been returned.`,
        {
          style: {
            fontSize: "16px",
            fontWeight: "bold",
          },
          icon: "💰",
        },
      );
    });

    return () => socket.off("single-card-timeout");
  }, []);

  if (!roomInfo) {
    return <LoadingPage />;
  }

  return (
    <>
      <div className="fixed left-0 right-0 top-0 z-50">
        <Navbar2 />
      </div>

      <div className="min-h-screen bg-[#082f52] flex flex-col items-center justify-center text-white p-4 pt-16">
        <div className="text-4xl font-mono my-3 text-green-400">
          0:{timeLeft?.toString().padStart(2, "0")}
        </div>

        <div className="font-serif bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] px-8 py-2 rounded-full mb-2 shadow-lg">
          {roomInfo?.amount} ብር ለካርድ
        </div>

        <div className="text-yellow-400 text-lg font-semibold mb-4 font-serif">
          የማሸነፍ ዕድልዎ: {roomInfo?.winAmount} ብር
        </div>

        <div className="grid gap-2 bg-[#2c4962] p-4 rounded-lg shadow-xl grid-cols-6 sm:grid-cols-16 md:grid-cols-18 lg:grid-cols-20">
          {cards.length === 0 ? (
            <div className="text-center text-gray-400 w-full col-span-full font-serif">
              Loading cards...
            </div>
          ) : (
            cards.map((card) => (
              <button
                key={card.number}
                onClick={() => handleCardClick(card.number)}
                disabled={reserving}
                className={`w-10 h-10 rounded-md text-sm font-bold transition-all 
                  ${
                    card.status === "confirmed"
                      ? "bg-green-500 text-white"
                      : card.status === "reserved"
                        ? "bg-red-500 text-white"
                        : "bg-gray-300 text-black hover:bg-yellow-300"
                  }`}
              >
                {card.number}
              </button>
            ))
          )}
        </div>
        <Footer />
      </div>
      {/* ✅ Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </>
  );
};

export default SelectCardsPage;
